import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import useTranslations from '~/components/translation/use-translations';
import Loading from '~/components/misc/loading';
import { Modal } from '~/components/modal/modal-old';
import Checkbox from '~/components/forms/checkbox';
import BinaryQuestion from '~/components/binary-question';
import currencyFormatter from '~/libs/currency-formatter';
import { selectTemporaryBuyIn } from '../../ducks/we-spin-selectors';
import { selectCurrency, selectLocale } from '~/components/account/ducks';
import { spectatorRegister } from '../../ducks/we-spin-duck';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import useLoginFlow from '~/hooks/use-login-flow';
import CheckboxContainer from '../binary-question-checkbox-container';
import { WE_SPIN_MODALS_TEXT_KEYS } from '../../constants/we-spin-text-keys';
import { showNotification } from '~/components/notifications/ducks/notifications';

/**
 * Modal for confirming buy-in to a WeSpin stream.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.openBuyin - Whether the buy-in modal should be shown.
 * @param {Function} props.closeBuyin - Function to close the modal.
 * @param {string} props.streamerName - Name of the streamer.
 * @param {Object} props.currentGame - Current game info object.
 * @param {string} props.streamRef - Unique stream reference ID.
 * @returns {JSX.Element}
 */
const BuyinModal = props => {
	const dispatch = useDispatch();
	const location = useLocation();
	const locale = useSelector(selectLocale);
	const buyInAmount = useSelector(selectTemporaryBuyIn(props.streamRef || ''));
	const playerCurrency = useSelector(selectCurrency);
	const authenticated = useSelector(authenticationSelector);
	const { initializeLogin } = useLoginFlow();
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_MODALS_TEXT_KEYS);

	const buyInBalance = useMemo(
		() => (buyInAmount > 0 ? currencyFormatter.format(buyInAmount || 0, playerCurrency, locale) : ''),
		[buyInAmount, playerCurrency, locale]
	);

	const [submitting, setSubmitting] = useState(false);
	const [isReminderChecked, setIsReminderChecked] = useState(true);

	const performBuyIn = useCallback(async () => {
		if (!authenticated) {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
			return;
		}

		try {
			setSubmitting(true);
			await dispatch(
				spectatorRegister(
					buyInAmount,
					props.streamRef,
					() => {
						dispatch(
							showNotification({
								openInSidebar: true,
								color: 'red',
								autoHide: true,
								messageKey: 'WE_SPIN_DASHBOARD_BUY_IN_OVERBALANCE_CAP_ERROR_NOTIFICATION'
							})
						);
					},
					() => {
						dispatch(
							showNotification({
								openInSidebar: true,
								color: 'red',
								autoHide: true,
								messageKey: 'WE_SPIN_DASHBOARD_BUY_IN_TO_LOW_BET_ERROR_NOTIFICATION'
							})
						);
					},
					() => {
						dispatch(
							showNotification({
								openInSidebar: true,
								color: 'red',
								autoHide: true,
								messageKey: 'WE_SPIN_DASHBOARD_BUY_IN_BALANCE_TO_LOW_NOTIFICATION'
							})
						);
					},
					isReminderChecked
				)
			);
			props.closeBuyin();
		} finally {
			setSubmitting(false);
		}
	}, [
		authenticated,
		buyInAmount,
		dispatch,
		initializeLogin,
		isReminderChecked,
		location.pathname,
		location.search,
		props
	]);

	if (isLoadingTranslations) return <Loading />;

	return (
		<Modal show={props.openBuyin} position="fixed">
			<BinaryQuestion
				shouldOpenBinary
				openInSidebar
				buttonsHasUncontrolledLoading={submitting}
				title={tr.WE_SPIN_BUY_IN_CONFIRM_TITLE}
				text={`${tr.WE_SPIN_BUY_IN_CONFIRM_MSG_ROW_ONE} ${props.streamerName} - ${props.currentGame?.gameName} ${tr.WE_SPIN_BUY_IN_CONFIRM_MSG_ROW_TWO}`}
				yesText={tr.WE_SPIN_BUY_IN_CONFIRM_BUTTON}
				noText={tr.WE_SPIN_BUY_IN_CONFIRM_CANCEL_BUTTON}
				onYesClick={performBuyIn}
				onNoClick={props.closeBuyin}
			>
				<div className="u-pt2">
					<table className="table--binary-question">
						<tbody>
							<tr>
								<td>{tr.WE_SPIN_BUY_IN_BALANCE_LABEL}</td>
								<td>{buyInBalance}</td>
							</tr>
							<tr>
								<td>{tr.WE_SPIN_BUY_IN_REMINDER}</td>
								<CheckboxContainer>
									<Checkbox
										id="remind-me-checkbox"
										isChecked={isReminderChecked}
										onChange={() => {
											// TODO: get value from event
											setIsReminderChecked(!isReminderChecked);
										}}
										iconPosition="right"
									/>
								</CheckboxContainer>
							</tr>
						</tbody>
					</table>
				</div>
			</BinaryQuestion>
		</Modal>
	);
};

export default BuyinModal;
